import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/**
 * Hand-typed entity contract — matches `EntityOut` in
 * backend/app/schemas.py. Will be replaced by OpenAPI codegen output in a
 * later phase; for Phase 0 we only need entities for the topbar selector.
 */
export type Entity = {
  id: string;
  name: string;
  short_code: string;
  country_code: string;
  state_province: string | null;
  status: 'ACTIVE' | 'INACTIVE' | string;
  is_deleted: boolean;
  currency_code?: string;
  fy_start_month?: number | null;
  // ...other fields exist server-side; we only declare what the UI uses.
};

export function useEntities() {
  return useQuery({
    queryKey: ['entities'],
    queryFn: () => api.get<Entity[]>('/entities'),
  });
}

/**
 * The workspace's only active entity, or null when there are several (or
 * they are still loading). Entity pickers hide themselves when this is set:
 * a choice with one answer is noise, and every page already scopes to it.
 */
export function useSoleEntity(): Entity | null {
  const { data } = useEntities();
  const active = (data ?? []).filter((e) => !e.is_deleted);
  return active.length === 1 ? active[0] ?? null : null;
}

export function useCurrentUser() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<{
      id: string;
      username: string;
      full_name?: string;
      email?: string;
      role: string;
      /** SIGNUP: standalone workspace. HRMS: integrated. null: single-database install. */
      workspace_origin?: string | null;
    }>('/auth/me'),
    staleTime: 5 * 60 * 1000,   // user info rarely changes during a session
  });
}

/** Whether this workspace is standalone (self-signup, no HR system behind it),
 *  as the server says now — not the sign-in record cached in the browser,
 *  which can predate the field. `null` until known. */
export function useIsStandaloneWorkspace(): boolean | null {
  const me = useCurrentUser();
  return me.data ? me.data.workspace_origin === 'SIGNUP' : null;
}

// ── Countries reference ─────────────────────────────────────────────────────

export type CountryPackStatus = 'production' | 'next' | 'roadmap';

export type Country = {
  code: string;
  name: string;
  flag: string;
  currency_code: string;
  currency_symbol: string;
  fy_start_month: number;
  fy_start_locked: boolean;
  default_timezone: string;
  /** Where this country's payroll pack is: shipped, next, or on the roadmap.
   *  Comes from the server (app.country_defaults.COUNTRY_PACK_ROADMAP) so the
   *  Add Entity dropdown and the Country Pack Status banner render from ONE
   *  list — the banner used to hold its own hard-coded array and advertised
   *  countries the dropdown did not offer. */
  pack_status: CountryPackStatus;
  /** What that pack covers, for the banner. Empty when nothing ships yet. */
  pack_scope: string;
};

export function useCountries() {
  return useQuery({
    queryKey: ['ref', 'countries'],
    queryFn: () => api.get<Country[]>('/ref/countries'),
    staleTime: 60 * 60 * 1000,  // ref data — rarely changes
  });
}

/** One IANA zone the server can load, with the UTC offset in force today. */
export type RefTimezone = { name: string; region: string; offset: string };

export function useTimezones() {
  return useQuery({
    queryKey: ['ref', 'timezones'],
    queryFn: () => api.get<RefTimezone[]>('/ref/timezones'),
    staleTime: 60 * 60 * 1000,  // ref data — offsets only move at DST changes
  });
}

// ── States / provinces reference ────────────────────────────────────────────

export type RefState = { code: string; name: string };

/**
 * States/provinces for a country (ISO 3166-2). Backed by
 * GET /api/ref/states?country_code=XX. Returns [] for countries without
 * a master list (callers should fall back to free-text entry).
 */
export function useStates(countryCode: string | null | undefined) {
  const cc = (countryCode || '').toUpperCase();
  return useQuery({
    queryKey: ['ref', 'states', cc],
    queryFn: () => api.get<RefState[]>(`/ref/states?country_code=${encodeURIComponent(cc)}`),
    enabled: !!cc,
    staleTime: 60 * 60 * 1000,
  });
}

// ── Entity CRUD ─────────────────────────────────────────────────────────────

export type EntityWritable = {
  name: string;
  short_code: string;
  country_code: string;
  state_province: string | null;
  entity_type: string | null;
  currency_code: string;
  fy_start_month: number;
  timezone: string;
  attendance_mode: 'MODULE' | 'LOP_IMPORT';
  status: 'SETUP' | 'ACTIVE' | 'INACTIVE';
  auto_create_employee_logins: boolean;
  default_employee_password: string;
  /** Sweep RR — LOP reversal config. window_months default 1 = previous
   *  calendar month only; up to 12 lets HR roll back ~a full FY. ESS
   *  enabled toggles whether employees can self-submit via the ESS
   *  portal (HR-only entities keep this OFF). */
  lop_reversal_window_months?: number;
  lop_reversal_ess_enabled?: boolean;
  /** Treat maternity leave ('ML') as a reversible LOP status. Default false
   *  (ML is paid leave — reversing it would double-pay). */
  lop_reversal_include_ml?: boolean;
  /** Auto-approve variable-pay awards created from the Salary Editor's
   *  "Variable Pay schedule" widget. Default false → awards land as DRAFT
   *  (awaiting per-period sign-off, correct for target-gated/sales variable
   *  pay). Flip ON for entities whose variable pay is guaranteed/fixed and
   *  should bundle into payroll without a manual approval click. */
  variable_pay_auto_approve?: boolean;
  /** Phase-0 reimbursement guard. When true (default) the Flexi basket
   *  page surfaces a soft warning if the employee has opted into the
   *  NEW tax regime — most FBP heads aren't exempt under Sec 115BAC.
   *  Flip off for entities that intentionally fund LTA-only / NPS-only
   *  baskets for NEW-regime employees. */
  fbp_warn_new_regime?: boolean;
};

export function useEntity(id: string | null | undefined) {
  return useQuery({
    queryKey: ['entities', 'detail', id],
    queryFn: () => api.get<Entity & { statutory_regs?: Record<string, unknown> }>(`/entities/${id}`),
    enabled: !!id,
  });
}

export function useCreateEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EntityWritable) => api.post<Entity>('/entities', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entities'] }),
  });
}

export function useUpdateEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: EntityWritable }) =>
      api.put<Entity>(`/entities/${vars.id}`, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entities'] }),
  });
}

export function useDeleteEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/entities/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entities'] }),
  });
}
